import React, { ReactElement, useState } from 'react';
import {
  ArgumentAxis,
  Chart,
  LineSeries,
  Tooltip,
  ValueAxis,
  ZoomAndPan,
} from '@devexpress/dx-react-chart-material-ui';
import { useMediaQuery, useTheme } from '@material-ui/core';
import {
  ValueScale,
  Animation,
  EventTracker,
  SeriesRef,
  ArgumentScale,
  Viewport,
} from '@devexpress/dx-react-chart';
import { getDateDiffInDays } from 'globals/utils';
import {
  amountTickFormatterForValueAxis,
  getTooltipContentComponent,
} from 'globals/graph-utils';
import { TileTabDataProps } from 'ui-elements/tabs/FsTileTabs/FsTileTab/FsTileTab';
import { getActiveColor } from 'components/UserPanel/Pages/Report/ReportCashflowChartsSection/ReportCashflowChartsSection.utils';
import {
  getInitialDayRange,
  getZoomLevel,
} from '../FsTrendChart/FsTrendChart.utils';
import {
  domainModifier,
  FsLineToggleChartSingleRowProps,
  GetMinMaxValue,
  scaleDateFactory,
} from './FsLineToggleChart.utils';
import {
  ArgumentAxisLabelComponent,
  TooltipOverlay,
  TooltipSheetComponent,
  ValueAxisGridComponent,
  ValueAxisLabelComponent,
} from './FsLineToggleChart.style-elements';

interface FsLineToggleChartProps {
  activeTabsIdxs: number[];
  populatedTabs: TileTabDataProps[];
  chartData: FsLineToggleChartSingleRowProps[];
  minMax: GetMinMaxValue;
}

const FsLineToggleChart: React.FC<FsLineToggleChartProps> = ({
  chartData,
  activeTabsIdxs,
  populatedTabs,
  minMax,
}) => {
  const [viewport, setViewport] = useState<Viewport>({});
  const [, setZoomLevel] = useState<number>(1);
  const initialDayRange = getInitialDayRange(chartData);
  const { min, max } = minMax;
  const theme = useTheme();
  const isUpSm = useMediaQuery(theme.breakpoints.up('sm'), {
    noSsr: true,
  });

  const changeViewport = (v: Viewport): void => {
    if (v.argumentStart && v.argumentEnd) {
      const newDayRange = getDateDiffInDays(v.argumentStart, v.argumentEnd);
      const zoomLevelValue = getZoomLevel(initialDayRange, newDayRange);
      if (zoomLevelValue < 20) {
        setZoomLevel(zoomLevelValue);
        setViewport(v);
      }
    }
  };

  // TODO: i need to get such cashflow-summary from API
  const currency = 'USD';
  const format = 'en-US';

  const [targetItem, setTargetItem] = useState<SeriesRef>();

  const changeTargetItem = (target: SeriesRef): void => {
    setTargetItem(target);
  };

  return (
    <Chart data={chartData} height={260}>
      {/* <p>ZOOM LEVEL: {zoomLevel}</p> */}

      {/* X */}
      <ArgumentScale name="valueScale" factory={scaleDateFactory()} />
      <ArgumentAxis
        showLine={false}
        labelComponent={ArgumentAxisLabelComponent}
      />

      {/* Y */}
      <ValueScale
        name="valueScale"
        modifyDomain={domainModifier(min - 1, max + 1)}
      />
      <ValueAxis
        showGrid
        scaleName="valueScale"
        labelComponent={ValueAxisLabelComponent}
        gridComponent={ValueAxisGridComponent}
        tickFormat={amountTickFormatterForValueAxis({ format, currency })}
      />

      {/* LINES */}
      {populatedTabs.map(({ id }, idx) => (
        <LineSeries
          name={`tab${idx}`}
          key={id}
          valueField={activeTabsIdxs.includes(idx) ? `tab${idx}` : `hidden`}
          argumentField="x"
          color={
            activeTabsIdxs.includes(idx) ? getActiveColor(idx) : 'transparent'
          }
          scaleName="valueScale"
        />
      ))}

      {/* ZOOM AND PAN */}
      {isUpSm && (
        <ZoomAndPan viewport={viewport} onViewportChange={changeViewport} />
      )}

      {/* EVENT TRACKER */}
      <EventTracker />

      {/* EVENT TRACKER */}
      <Tooltip
        targetItem={targetItem}
        onTargetItemChange={changeTargetItem}
        sheetComponent={TooltipSheetComponent}
        overlayComponent={(props): ReactElement<Tooltip.OverlayProps> => (
          <TooltipOverlay {...props} />
        )}
        contentComponent={getTooltipContentComponent({
          chartData,
          currency,
          format,
        })}
      />

      {/* ANIMATION */}
      <Animation />
    </Chart>
  );
};

export default FsLineToggleChart;
